import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Clock, AlertCircle, User, Flag } from 'lucide-react';
import { Task } from '../../hooks/useTasks';
import { format } from 'date-fns';

interface TaskCardProps {
  task: Task;
  onClick: () => void;
}

const TaskCard: React.FC<TaskCardProps> = ({ task, onClick }) => {
  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'completed':
        return {
          icon: CheckCircle2,
          color: 'text-green-400',
          bg: 'bg-green-500/10',
          border: 'border-green-500/20',
        };
      case 'in_progress':
        return {
          icon: Clock,
          color: 'text-[rgb(var(--brand))]',
          bg: 'bg-blue-500/10',
          border: 'border-blue-500/20',
        };
      default:
        return {
          icon: AlertCircle,
          color: 'text-[rgb(var(--muted))]',
          bg: 'bg-gray-500/10',
          border: 'border-gray-500/20',
        };
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'text-red-400';
      case 'medium':
        return 'text-yellow-400';
      default:
        return 'text-[rgb(var(--muted))]';
    }
  };

  const statusConfig = getStatusConfig(task.status);
  const StatusIcon = statusConfig.icon;
  
  // Formater la date en évitant les problèmes de timezone
  const formattedDate = task.due_date ? (() => {
    // Si c'est déjà au format YYYY-MM-DD, extraire directement
    if (task.due_date.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = task.due_date.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      return format(date, 'MMM dd');
    }
    // Sinon, parser normalement
    return format(new Date(task.due_date), 'MMM dd');
  })() : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      whileHover={{ scale: 1.02, y: -4 }}
      transition={{ duration: 0.2 }}
      onClick={onClick}
      className="theme-card-bg theme-card-border rounded-lg p-4 cursor-pointer transition-all duration-200 group border-2 shadow-[0_4px_12px_rgba(0,0,0,0.15)] dark:shadow-[0_8px_24px_rgba(0,0,0,0.4)] hover:theme-hover-card hover:shadow-[0_6px_16px_rgba(0,0,0,0.2)] dark:hover:shadow-[0_12px_32px_rgba(0,0,0,0.5)]"
    >
      <div className="flex items-start justify-between mb-3">
        <h3 className="theme-text-primary font-semibold text-base flex-1 line-clamp-2 group-hover:text-[#007acc] dark:group-hover:text-[#4da6ff] transition-colors">
          {task.title}
        </h3>
        <div className={`${statusConfig.bg} ${statusConfig.border} border-2 rounded-lg px-2.5 py-1 ml-2 flex items-center gap-1.5 shadow-sm group-hover:shadow-md transition-shadow`}>
          <StatusIcon className={`w-3.5 h-3.5 ${statusConfig.color}`} />
          <span className={`text-xs font-medium ${statusConfig.color} capitalize`}>
            {task.status.replace('_', ' ')}
          </span>
        </div>
      </div>

      {task.description && (
        <p className="theme-text-secondary text-sm mb-3 line-clamp-2">
          {task.description}
        </p>
      )}

      <div className="flex items-center justify-between mt-3 pt-3 border-t theme-card-border">
        <div className="flex items-center gap-3">
          {task.order && (
            <span className="text-xs px-2.5 py-1 rounded-lg bg-[#9c68f2]/10 text-[#9c68f2] border border-[#9c68f2]/30 shadow-sm group-hover:bg-[#9c68f2]/15 group-hover:border-[#9c68f2]/40 transition-all">
              {task.order.title}
            </span>
          )}
          {task.priority && (
            <div className="p-1.5 rounded-lg bg-gray-500/10 group-hover:bg-gray-500/20 transition-colors">
              <Flag className={`w-3.5 h-3.5 ${getPriorityColor(task.priority)}`} />
            </div>
          )}
        </div>
        
        {formattedDate && (
          <span className="text-xs theme-text-tertiary flex items-center gap-1.5 px-2 py-1 rounded-lg bg-gray-500/5 group-hover:bg-gray-500/10 transition-colors">
            <Clock className="w-3.5 h-3.5" />
            {formattedDate}
          </span>
        )}
      </div>
    </motion.div>
  );
};

export default TaskCard;

